import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { CalendarEventFlags, CalendarEventTypes } from '@core/enums/calendar-event-type.enum';
import { CaseDetails, LeaveInfo } from '@core/models';
import { LayoutService } from '@core/services';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { LeaveAdminStoreService } from '@core/services/leave-admin-store.service';
import { EditCalendarTimeComponent } from '@modules/dialogs/edit-calendar-time/edit-calendar-time.component';
import { ViewCalendarItemComponent, ViewCalendarItemComponentData } from '@modules/dialogs/view-calendar-item/view-calendar-item.component';
import { 
  CalendarOptions, 
  Calendar, 
  EventApi, 
  EventSourceFuncArg, 
  EventInput, 
  EventContentArg, 
  ViewMountArg } from '@fullcalendar/core';
import dayGridMonth from '@fullcalendar/daygrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction'
import { ManageEmployeesService } from '@core/services/manage-employees.service';
import { 
  LeaveCalendarPageData, 
  EmployeeCalendarPageDataGet, 
  CaseDetailsCalendarPageDataGet, 
  CustomCalendarEventProps, 
  EmployeeLeaveHoursExtended} from '@core/models/leave-admin/leave-calendar/leave-calendar.model';
import { EmployeeRecord } from '@core/models/leave-admin/employees/employee-record.model';
import { AddEditCalendarWorkdayComponent } from '@modules/dialogs/add-edit-calendar-workday/add-edit-calendar-workday.component';
import { LeaveAdminService } from '@core/services/leave-admin.service';
import { Router } from '@angular/router';
import { appRoutePaths, leaveAdminRoutes } from '@core/routes/route-paths.constants';
import { EmployeeRecordStoreService } from '@core/services/employee-record-store.service';
import { VerboseFormattingArg } from '@fullcalendar/core/internal';
import { indicate } from '@shared/helpers';

@Component({
    selector: 'app-leave-calendar',
    templateUrl: './leave-calendar.component.html',
    styleUrls: ['./leave-calendar.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LeaveCalendarComponent implements OnInit {
  static readonly componentName = 'LeaveCalendarComponent';
  
  @Input() tabChangeIndex: Subject<number>;
  @Input() forceCalendarRefresh: Subject<boolean>;
  @Input() caseDetails: CaseDetails;
  @Input() employeeRecord: EmployeeRecord;
  @Input() cbShowCalendarWarning: () => void;

  @Input() sysText: any;
  @Input() unsavedChangesSysText: any;
  @Input() editCalendarTimeSysText: any;
  @Input() viewCalendarItemSysText: any;
  @Input() addEditCalWorkdaySysText: any;
  @Input() reviewRequestSysText: any;

  @Output() isEditing = new EventEmitter<boolean>();

  public tabIndex: number;
  public hasShownWarning: boolean = false;
  public isLoading: boolean = false;
  public calendarOptions: CalendarOptions;
  public isSubmitting$: Subject<boolean> = new Subject<boolean>();  

  private calendar: Calendar;
  private hasBeenInitialized: boolean = false;
  private blockGetEvents: boolean = false;
  private destroy$: Subject<void> = new Subject<void>();
  private calendarNeedsUpdate: boolean = false;
  private currentView: string;
  private forceRefresh: boolean = false;

  constructor(
    private datePipe: DatePipe,
    private layoutService: LayoutService,
    private caseDetailsStore: LeaveAdminStoreService,
    private employeeRecordStore: EmployeeRecordStoreService,
    private dialog: MatDialog,
    private leaveAdminStore: LeaveAdminService,
    private manageEmployeesService: ManageEmployeesService,
    private router: Router,
    private leaveAdminService: LeaveAdminService 
  ) {
    this.calendarOptions = this.createCalendarOptions();
  }

  ngOnInit(): void {
    this.initCal();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // #region cal functions

  private initCal(): void {
    if (this.caseDetails == null)
      this.calendarOptions.footerToolbar["center"] = this.calendarOptions.footerToolbar["center"].replace(' listLeaveHourExtents', '');
    
    this.forceCalendarRefresh
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        this.forceRefresh = res;
      });
    
    this.tabChangeIndex
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        if (res == null || this.tabIndex == res) {
          // the tab index is poked (and not changed) by some operations
          // (e.g. adding intermittent time or spreading hours) to trigger
          // a refresh of the calendar

          this.calendarNeedsUpdate = true;
        }

        if (res != null && this.tabIndex != res) {
          this.tabIndex = res;

          // force an update if returning to the calendar tab
          // (this is a work-around to the calendar appearing
          // like a thumbnail when returning to the calendar
          // after an update to leave information)

          if (this.hasBeenInitialized && this.tabIndex == 1 && this.forceRefresh) {
            this.forceRefresh = false;
            this.calendarNeedsUpdate = true;
          }
        }

        if (!this.hasBeenInitialized && this.tabIndex == 1) {
          this.hasBeenInitialized = true;
          setTimeout(() => {
            // first time viewing the calendar tab since the case details were 
            // loaded, so initialize the calendar 

            this.renderCal(true);
          }, 0);
        } 
        else if (this.hasBeenInitialized && this.calendarNeedsUpdate) {
          this.renderCal();
        }
        
        if (this.caseDetails?.showCalendarOverlayForAdmins 
            && (this.caseDetails.leaveCalendar?.missingCalendarRequirements 
              || this.caseDetails.leaveCalendar?.hasLeaveHoursErrors) 
            && !this.hasShownWarning && this.tabIndex == 1) {
          // first time on the calendar tab since loading case details for a 
          // case with missing calendar requirements, so show the warning dialog 

          this.hasShownWarning = true;
          this.cbShowCalendarWarning();
        }
    });

    if (this.employeeRecord) {
      this.employeeRecordStore.employeeRecord$
        .pipe(takeUntil(this.destroy$))
        .subscribe((res)=> {
          this.employeeRecord = res;

          if (res != null && this.hasBeenInitialized) {
            this.calendarNeedsUpdate = true;
            this.renderCal();
          }
        });
    } 
    else if (this.caseDetails) {
      this.caseDetailsStore.caseDetails$
        .pipe(takeUntil(this.destroy$))
        .subscribe((res)=> {
          this.caseDetails = res;

          if (res != null && this.hasBeenInitialized) {
            this.calendarNeedsUpdate = true;
            this.renderCal();
          }
        });
    }
  }

  private createCalendarOptions(): CalendarOptions {
    const calendarOptions: CalendarOptions = {
      // listPlugin provides support for "list" modes (e.g. list1Month)

      plugins: [ listPlugin, dayGridMonth, interactionPlugin ],

      // displayEventTime avoids showing "all-day" on every event when in list mode

      displayEventTime: false, 
      initialView: this.layoutService.isMobile
        ? 'list1Month'
        : 'calendar1Month',
      height: '100%', // fill .calendar-bg, which the stylesheet sizes to the screen; the grid takes what the toolbars leave

      eventClick: (info) => {
        this.eventClick(info.event, info.jsEvent);
      },

      datesSet: (event) => {
        const midDate = new Date((event.start.getTime() + event.end.getTime()) / 2);
        this.setStickyCalendarDate(midDate);

        // FullCalendar gives every toolbar button aria-pressed, so screen readers call prev / next / today
        // "toggle button, not pressed"; only the view buttons are toggles. It never sets the attribute again
        // on buttons it keeps (their pressed state never changes)

        this.calendar?.el.querySelectorAll('.fc-prev-button, .fc-next-button, .fc-today-button')
          .forEach(button => button.removeAttribute('aria-pressed'));
      },

      headerToolbar: {
        start: 'prev',
        center: 'title',
        end: this.layoutService.isMobile
          ? 'next'
          : 'today next'
      },

      footerToolbar: {
        // listLeaveHourExtents will be removed from the toolbar during init
        // if the calendar isn't for a case

        center: this.layoutService.isMobile
          ? 'calendar1Month list1Month listLeaveHourExtents today'
          : 'calendar1Month list1Month list1Year listLeaveHourExtents'
      },

      listDaySideFormat: {
        weekday: 'short'
      },

      // using eventDidMount to set text properties for background 
      // events (setting directly by event properties not supported) 

      eventDidMount: function(info) {
        if (info.event.textColor) {
          info.el.style.color = info.event.textColor;
        }
        if (info.event.extendedProps.opacity) {
          info.el.style.opacity = info.event.extendedProps.opacity;
        }

        // FullCalendar makes a clickable event focusable (a tabindex on an <a> with no href: the
        // event itself in calendar view, its title link in list views) but gives it no role, so
        // screen readers don't say it's a button

        const focusableEl = info.el.hasAttribute('tabindex') ? info.el : info.el.querySelector('a[tabindex]');
        focusableEl?.setAttribute('role', 'button');
      },

      // get calendar events in response to the calendar request 
      // for events (which provides a timeframe) 

      events: (arg: EventSourceFuncArg) => {
        return this.getEvents(arg);
      },

      loading: (isLoading) => {
        if (this.hasBeenInitialized && isLoading) {
          this.isLoading = true;
        } else {
          this.isLoading = false;
        }

        if (!isLoading) {
          // once the events are drawn, have FullCalendar measure again: they can make the month too tall for its box,
          // and FullCalendar only makes room for the scrollbar when it measures (otherwise the scrollbar covers Saturday)
          requestAnimationFrame(() => this.calendar?.updateSize());
        }
      },

      // handle custom event html

      eventContent: (arg) => {
        return this.eventContent(arg);
      },

      viewDidMount: (arg) => {
        this.currentView = arg.view.type;
      },

      // handle switching views

      viewWillUnmount: (arg) => {
        return this.viewWillUnmount(arg);
      },

      eventOrder: (a: EventApi, b: EventApi) => {
        return this.eventOrder(a, b);
      },

      // TODO - something was causing sticky headers to glitch out

      stickyHeaderDates: false,

      buttonText: { today: 'Today' },

      views: {
        calendar1Month: {
          type: 'dayGridMonth',
          duration: { months: 1 },
          titleFormat: (arg) => {
            return this.monthTitleFormat(arg);
          },
          buttonText: 'Calendar'
        },

        list1Month: {
          type: 'list',
          duration: { months: 1 },
          titleFormat: (arg) => {
            return this.monthTitleFormat(arg);
          },
          buttonText: 'Month'
        },

        list1Year: {
          type: 'list',
          duration: { years: 1 },
          buttonText: 'Year'
        },

        listLeaveHourExtents: {
          type: 'list',
          visibleRange: (arg) => {
            return this.leaveHourExtentsVisibleRange(arg);
          },
          titleFormat: (arg) => {
            return this.leaveHourExtentsTitleFormat(arg);
          },
          buttonText: 'Extents'
        }
      }
    };
    
    return calendarOptions;
  }

  private renderCal(goToCaseDate: boolean = false): void {
    /* renderCal will be called for two cases:
    * 1) the calendar is being initialized for the first time after
    *    retrieving a case (calendarNeedsUpdate == false)
    * 2) an operation has changed data that has potentially affected
    *    the calendar (calendarNeedsUpdate == true)
    */

    if (!this.calendar) {
      const calendarEl = document.getElementById('calendar');

      if (!calendarEl) {
        // the calendar tab's content isn't attached to the DOM yet
        // (mat-tab-group lazily renders tab content); wait a frame and
        // retry instead of handing FullCalendar a null element
        if (!this.destroy$.closed) {
          requestAnimationFrame(() => this.renderCal(goToCaseDate));
        }
        return;
      }

      // 'new Calendar' causes 'getEvents' to fire, so set 'blockGetEvents'
      // flag to block getEvents from getting data twice - getEvents will
      // fire again on 'gotoDate'

      this.blockGetEvents = true;
      this.calendar = new Calendar(calendarEl, this.calendarOptions);
      this.blockGetEvents = false;
    }

    if (this.calendarNeedsUpdate) {
      this.calendarNeedsUpdate = false;

      if (this.calendar.view.type == 'listLeaveHourExtents') {
        // reset the view so the calendar title updates

        this.calendar.changeView('listLeaveHourExtents', this.leaveHourExtentsVisibleRange(this.calendar.getDate()));
      }

      this.calendar.removeAllEvents();
      this.calendar.refetchEvents();  

      return;
    }

    let targetCalendarDate = this.getStickyCalendarDate();
    
    if (!targetCalendarDate) {
      targetCalendarDate = new Date();
    }

    if (goToCaseDate 
      && this.caseDetails?.leaveInformation.startDate != null 
      && targetCalendarDate < new Date(this.caseDetails?.leaveInformation.startDate)) {
      this.calendar.gotoDate(new Date(this.caseDetails?.leaveInformation.startDate));
    }
    else if (goToCaseDate 
      && this.caseDetails?.leaveInformation.endDate != null 
      && targetCalendarDate > new Date(this.caseDetails?.leaveInformation.endDate)) {
      this.calendar.gotoDate(new Date(this.caseDetails?.leaveInformation.endDate));
    }
    else {
      this.calendar.gotoDate(targetCalendarDate);
    }

    this.calendar.render();
  }

  private setStickyCalendarDate(stickyCalendarDate: Date) {
    sessionStorage.setItem("stickyCalendarDate", stickyCalendarDate.toLocaleDateString());
  }

  private getStickyCalendarDate(): Date {
    let stickyCalendarDate: Date;
    const lastStickyCalendarDate = sessionStorage.getItem("stickyCalendarDate");

    if (lastStickyCalendarDate) {
      stickyCalendarDate = new Date(lastStickyCalendarDate);
    }

    return stickyCalendarDate;
  }

  private viewWillUnmount(arg: ViewMountArg) {
    let events = arg.view.calendar.getEvents();

    const displayValue = arg.view.type.startsWith('calendar')
      ? 'block'
      : 'background';

    events.forEach((event) => {
      const customProps = event.extendedProps.customProps as CustomCalendarEventProps;

      // when switching FROM calendar TO list, handle 
      // changing all background events to block since 
      // background events will not render in list view

      // when switching FROM list TO calendar, handle 
      // changing 'cell' event's display to 'background'

      if (customProps?.eventType == CalendarEventTypes.BgWorkdayCell 
        || customProps?.eventType == CalendarEventTypes.BgNonWorkdayCell
        || customProps?.eventType == CalendarEventTypes.BgBlankCell
        || customProps?.eventType == CalendarEventTypes.BgBlankCellNoTitle) {
        event.setProp('display', displayValue);
      }
    });
  }

  private setCalFocus(): void {
    // the calendar click event does not remove focus from the mat case calendar tab
    // so we set focus to the calendar here manually: its title ("October 2026"), which
    // screen readers announce briefly (the whole calendar div made them read everything);
    // tabindex -1 lets code focus it without adding a Tab stop

    const calTitle = document.querySelector<HTMLElement>('#calendar .fc-toolbar-title');

    if (calTitle) {
      calTitle.tabIndex = -1;
      calTitle.focus();
    }
  }

  private focusDayCell(event: EventApi): void {
    // after a save the calendar redraws every event, so focus goes to the edited day's cell,
    // which FullCalendar keeps and screen readers announce by its date (tabindex -1 lets code
    // focus it without adding a Tab stop); list views have no day cells, so they use the title

    const dayCell = document.querySelector<HTMLElement>(`#calendar .fc-daygrid-day[data-date="${event.startStr}"]`);

    if (dayCell) {
      dayCell.tabIndex = -1;
      dayCell.focus();
    } else {
      this.setCalFocus();
    }
  }

  private isMouseClick(jsEvent: Event): boolean {
    // Enter or Space on a focused event (a KeyboardEvent from FullCalendar, or a click with
    // detail 0 from a button) should leave focus there, so the dialog returns focus to it

    return jsEvent instanceof MouseEvent && jsEvent.detail > 0;
  }

  private checkForOverdraw(cdli: LeaveInfo, xhours: EmployeeLeaveHoursExtended): boolean {
    return (cdli?.isBank1HoursApplicable && xhours.employeeBank1AvailableHours != null && xhours.employeeBank1AvailableHours < 0) 
    || (cdli?.isBank2HoursApplicable && xhours.employeeBank2AvailableHours != null && xhours.employeeBank2AvailableHours < 0) 
    || (cdli?.isBank3HoursApplicable && xhours.employeeBank3AvailableHours != null && xhours.employeeBank3AvailableHours < 0) 
    || (cdli?.isBank4HoursApplicable && xhours.employeeBank4AvailableHours != null && xhours.employeeBank4AvailableHours < 0) 
    || (cdli?.isBank5HoursApplicable && xhours.employeeBank5AvailableHours != null && xhours.employeeBank5AvailableHours < 0);
  }

  private checkForExhausted(cdli: LeaveInfo, xhours: EmployeeLeaveHoursExtended): boolean {
    let exhasuted = false;
    if (cdli?.leaveTypeRequireAllBankHours) {
      exhasuted = 
        (cdli?.isBank1HoursApplicable && xhours.employeeBank1AvailableHours != null && xhours.employeeBank1AvailableHours == 0) 
        || (cdli?.isBank2HoursApplicable && xhours.employeeBank2AvailableHours != null && xhours.employeeBank2AvailableHours == 0) 
        || (cdli?.isBank3HoursApplicable && xhours.employeeBank3AvailableHours != null && xhours.employeeBank3AvailableHours == 0) 
        || (cdli?.isBank4HoursApplicable && xhours.employeeBank4AvailableHours != null && xhours.employeeBank4AvailableHours == 0) 
        || (cdli?.isBank5HoursApplicable && xhours.employeeBank5AvailableHours != null && xhours.employeeBank5AvailableHours == 0)
    } else {
      exhasuted = 
        (cdli?.isBank1HoursApplicable 
          || cdli?.isBank2HoursApplicable 
          || cdli?.isBank3HoursApplicable 
          || cdli?.isBank4HoursApplicable
          || cdli?.isBank5HoursApplicable)
        && (!cdli?.isBank1HoursApplicable || (xhours.employeeBank1AvailableHours != null && xhours.employeeBank1AvailableHours == 0))
        && (!cdli?.isBank2HoursApplicable || (xhours.employeeBank2AvailableHours != null && xhours.employeeBank2AvailableHours == 0))
        && (!cdli?.isBank3HoursApplicable || (xhours.employeeBank3AvailableHours != null && xhours.employeeBank3AvailableHours == 0))
        && (!cdli?.isBank4HoursApplicable || (xhours.employeeBank4AvailableHours != null && xhours.employeeBank4AvailableHours == 0))
        && (!cdli?.isBank5HoursApplicable || (xhours.employeeBank5AvailableHours != null && xhours.employeeBank5AvailableHours == 0))
    }
    return exhasuted;
  }

  // #endregion cal functions

  // #region event functions

  private async getEvents(args: EventSourceFuncArg): Promise<EventInput[]> {
    // get calendar page data (employee record or case details)

    const calendarStartDate = new Date(args.start);
    const calendarEndDate = new Date(args.end);
    const cd = this.caseDetails;
    const er = this.employeeRecord;
    
    let calendarPageData: LeaveCalendarPageData = null;

    if (this.hasBeenInitialized && !this.blockGetEvents && er) {
      const dto: EmployeeCalendarPageDataGet = {
        employeeId: er.employeeId,
        calendarStartDate: calendarStartDate,
        calendarEndDate: calendarEndDate
      };

      calendarPageData = await this.manageEmployeesService.getEmployeeCalendarPageData(dto)
        .toPromise()
        .catch(() => {}) as LeaveCalendarPageData;
    } 
    else if (this.hasBeenInitialized && !this.blockGetEvents && cd) {
      const dto: CaseDetailsCalendarPageDataGet = {
        caseId: cd.leaveInformation.caseId,
        calendarStartDate: calendarStartDate,
        calendarEndDate: calendarEndDate
      };

      calendarPageData = await this.leaveAdminStore.getCaseDetailsCalendarPageData(dto)
        .toPromise()
        .catch(() => {}) as LeaveCalendarPageData;
    }

    // build and return events

    return new Promise<EventInput[]> (
      (resolve) => {
        let events: EventInput[] = [];

        if (!calendarPageData) {
          resolve(events);
        }

        const calendarStartDateFormed = this.datePipe.transform(new Date(calendarStartDate), 'yyyy-MM-dd');
        const calendarEndDateFormed = this.datePipe.transform(new Date(calendarEndDate), 'yyyy-MM-dd');
        const cdli = cd?.leaveInformation;
        const caseStartDate = cdli?.startDate ? new Date(cdli?.startDate) : null;
        const caseEndDate = cdli?.endDate ? new Date(cdli?.endDate) : null;
        const caseStartDateFormed = caseStartDate ? this.datePipe.transform(new Date(caseStartDate), 'yyyy-MM-dd') : null;
        const caseEndDateFormed = caseEndDate ? this.datePipe.transform(new Date(caseEndDate), 'yyyy-MM-dd'): null;
        let nonBlankCells: string[] = [];

        // first create work calendar cell events 

        this.createWorkCalendarCellEvents(calendarPageData, nonBlankCells, events);
        
        // then 'normal' events are sorted from top to bottom 
        // in the order they are created

        this.createHoursWorkedEvents(
          calendarPageData, 
          events, 
          calendarStartDateFormed, 
          calendarEndDateFormed);

        if (cd?.leaveCalendar?.canChangeHours) {
          this.createAddHoursEvents(
            calendarPageData, 
            cdli, 
            events, 
            calendarStartDate, 
            calendarEndDate, 
            caseStartDateFormed, 
            caseEndDateFormed);
        }

        this.createLeaveHoursRequestEvents(
          calendarPageData, 
          events, 
          calendarStartDateFormed, 
          calendarEndDateFormed);

        this.createHoursEvents(
          calendarPageData, 
          cd, 
          events, 
          calendarStartDateFormed, 
          calendarEndDateFormed);

        this.createActionItemEvents(
          calendarPageData, 
          events, 
          calendarStartDateFormed, 
          calendarEndDateFormed);

        this.createSpacerEvents(
          calendarPageData, 
          events, 
          calendarStartDate, 
          calendarEndDate);

        // then create blank cell events

        this.createBlankCellEvents(
          calendarPageData, 
          cd, 
          nonBlankCells, 
          events, 
          calendarStartDate, 
          calendarEndDate);

        // lastly flag events 

        this.flagCellEvents(
          calendarPageData, 
          events, 
          cdli, 
          calendarStartDateFormed, 
          calendarEndDateFormed, 
          caseStartDateFormed, 
          caseEndDateFormed);

        // only clickable events (the ones with a pointer cursor) take keyboard focus; otherwise
        // FullCalendar makes every event a Tab stop, including the empty spacer on every day

        events.forEach(event => event.interactive = (event.classNames as string[]).includes('calendar-pointer'));

        resolve(events);
      }
    );
  }

  private eventOrder(a: EventApi, b: EventApi): number {
    // for list view, sort 'cell' events to the top and sort hours events by case id 

    // -1 == a then b, 1 == b then a
    var sortOrder = 1;

    const customPropsA = a.extendedProps.customProps as CustomCalendarEventProps;
    const customPropsB = b.extendedProps.customProps as CustomCalendarEventProps;

    if (customPropsA?.eventType == CalendarEventTypes.BgWorkdayCell 
      || customPropsA?.eventType == CalendarEventTypes.BgNonWorkdayCell
      || customPropsA?.eventType == CalendarEventTypes.BgBlankCell
      || customPropsA?.eventType == CalendarEventTypes.BgBlankCellNoTitle) {
      sortOrder = -1;
    }
    
    if ((customPropsA.employeeLeaveHoursExtended?.leaveCaseID 
        > customPropsB.employeeLeaveHoursExtended?.leaveCaseID)
      && (customPropsB?.eventType != CalendarEventTypes.BgWorkdayCell 
        && customPropsB?.eventType != CalendarEventTypes.BgNonWorkdayCell
        && customPropsB?.eventType != CalendarEventTypes.BgBlankCell
        && customPropsB?.eventType != CalendarEventTypes.BgBlankCellNoTitle)) {
      sortOrder = -1;
    }
    
    // make sure the spacer is at the bottom

    if (customPropsA?.eventType == CalendarEventTypes.BlankSpacer) {
      sortOrder = 1;
    }
    else if (customPropsB?.eventType == CalendarEventTypes.BlankSpacer) {
      sortOrder = -1;
    }

    return sortOrder;
  }

  private eventContent(arg: EventContentArg) {  
    const customProps = arg.event.extendedProps.customProps as CustomCalendarEventProps;
    
    // handle '* cell background' events w clickable title

    if (customProps?.eventType == CalendarEventTypes.BgWorkdayCell 
      || customProps?.eventType == CalendarEventTypes.BgNonWorkdayCell
      || customProps?.eventType == CalendarEventTypes.BgBlankCell
      || customProps?.eventType == CalendarEventTypes.BgBlankCellNoTitle) {
      const flexContainer = document.createElement('div');
      flexContainer.className = 'fc-event-flex-container';

      // a clickable title is a button, so it can be reached and used with the keyboard

      const canEditWorkday = this.employeeRecord || this.caseDetails?.leaveCalendar?.canChangeWorkdays;
      const titleElement = document.createElement(canEditWorkday ? 'button' : 'span');
      titleElement.textContent = arg.event.title;
      titleElement.className = 'fc-event-title custom-event-title';

      if (customProps?.eventType == CalendarEventTypes.BgBlankCellNoTitle) {
        titleElement.className += ' custom-event-blank-title';
      }

      if (canEditWorkday) {
        titleElement.setAttribute('type', 'button');
        titleElement.className += ' button-reset cell-title-hover calendar-pointer';

        // "..." (blank cells) and empty titles say nothing, so they're named by what they do

        if (customProps?.eventType == CalendarEventTypes.BgBlankCell || !arg.event.title.trim()) {
          titleElement.setAttribute('aria-label', this.sysText.setWorkday_ariaLabel);
        }

        titleElement.addEventListener('click', (event) => {
          event.stopPropagation();
          event.preventDefault();
          this.onTitleClick(arg.event, event);
        }, false);
      }

      flexContainer.appendChild(titleElement);

      // ...handle adding flag text to bottom of cell

      if (customProps?.eventFlag && customProps?.flagText) {
        const exhaustedDiv = document.createElement('div');
        exhaustedDiv.className = 'fc-event-title fc-flag-text';
        exhaustedDiv.textContent = customProps.flagText;
        flexContainer.appendChild(exhaustedDiv);
      }

      return {domNodes: [flexContainer]};
    }

    // otherwise handle adding case id links

    // ...hours events (never clickable on the employee record, so the link can take focus)

    else if (this.employeeRecord && customProps?.eventType == CalendarEventTypes.Hours) {
      return this.createCaseLink(arg.event.title, customProps.employeeLeaveHoursExtended.leaveCaseID, true);
    }

    // ...request for hours events (in calendar view the event itself is a button, and a link
    // can't be nested in it; in list views the custom content leaves the row unfocusable)

    else if (this.employeeRecord
      && customProps?.eventType == CalendarEventTypes.RequestForLeaveHours
      && customProps.employeeLeaveHoursRequests[0]) {
      return this.createCaseLink(arg.event.title, customProps.employeeLeaveHoursRequests[0].caseId, arg.view.type.startsWith('list'));
    }

    // otherwise handle adding 'cal summary' text to blank
    // spacer events for list view if xhours exist (in calendar
    // view the spacer is an empty clickable strip, so the text
    // is visually hidden and only names it)

    else if (customProps?.eventType == CalendarEventTypes.BlankSpacer
      && customProps.employeeLeaveHoursExtended) {
      if (!this.caseDetails) {
        return '';
      }

      // FullCalendar's own content is a focusable link showing the title (the summary text)

      if (arg.view.type.startsWith('list')) {
        return true;
      }

      const hiddenName = document.createElement('span');
      hiddenName.className = 'cdk-visually-hidden';
      hiddenName.textContent = this.sysText.calSummary;
      return {domNodes: [hiddenName]};
    }
    
    // otherwise just pass through event title text as normal (in list views FullCalendar's own
    // content, a focusable link that Enter or Space opens, which custom content would drop; calendar
    // view keeps the plain title, since its default content looks different)

    else {
      return arg.view.type.startsWith('list') ? true : arg.event.title;
    }
  }

  private eventClick(event: EventApi, jsEvent: Event): void {
    if (this.isMouseClick(jsEvent)) {
      this.setCalFocus();
    }

    const eventProps = event.extendedProps.customProps as CustomCalendarEventProps;

    // show a dialog to add or edit the hours 

    if (this.caseDetails
        && (eventProps.eventType == CalendarEventTypes.Hours
          || eventProps.eventType == CalendarEventTypes.AddHours)) {
      if (this.caseDetails.leaveCalendar?.canChangeHours) {
        const dialogConfig: MatDialogConfig = {
          id: EditCalendarTimeComponent.componentName,
          width: '450px',
          panelClass: "mat-dialog-container-mobileWidth",
          autoFocus: 'input', // the field, not the header X shown on phones
          data: { 
            event: event,
            caseDetails: this.caseDetails,
            hoursExtended: eventProps.employeeLeaveHoursExtended,
            workCalendarDay: eventProps.workCalendarDay,
            sysText: this.editCalendarTimeSysText,
            unsavedChangesSystext: this.unsavedChangesSysText
          }
        };
  
        this.dialog.open(EditCalendarTimeComponent, dialogConfig)
          .afterClosed()
          .pipe(filter((res) => !!res))
          .subscribe(res => {
            // updating the store object will trigger the subscription to the
            // store object and cause the calendar to update (it redraws every
            // event, so focus goes to the day's cell instead of being lost)

            this.focusDayCell(event);
            this.caseDetailsStore.caseDetails = res;
          });
        }
    }

    // show a dialog of the event (action item or case event)

    else if (eventProps.eventType == CalendarEventTypes.ActionItem
      || eventProps.eventType == CalendarEventTypes.HoursWorked) {
      this.dialog.open<ViewCalendarItemComponent, ViewCalendarItemComponentData>(ViewCalendarItemComponent, {
        id: ViewCalendarItemComponent.componentName,
        width: "450px",
        panelClass: "mat-dialog-container-mobileWidth",
        disableClose: false,
        data: {
          event: event,
          sysText: this.viewCalendarItemSysText,
          employeeRecord: null,
          caseDetails: null,
          unsavedChangesSysText: null,
          reviewRequestSysText: null,
          leaveYearOptions: null,
          workday: null
        }
      });
    }

    // handle leave hours requests events

    else if (eventProps.eventType == CalendarEventTypes.RequestForLeaveHours) {
      this.isLoading = true;

      this.leaveAdminService.getLeaveCaseDetails(eventProps.employeeLeaveHoursRequests[0].caseId, this.caseDetails)
        .pipe(indicate(this.isSubmitting$))
        .subscribe((res) => {
          this.isLoading = false;
          
          const dialogRef = this.dialog.open<ViewCalendarItemComponent, ViewCalendarItemComponentData>(ViewCalendarItemComponent, {
            id: ViewCalendarItemComponent.componentName,
            width: "450px",
            panelClass: "mat-dialog-container-mobileWidth",
            disableClose: false,
            data: {
              event: event,
              sysText: this.viewCalendarItemSysText,
              employeeRecord: this.employeeRecord,
              caseDetails: res,
              unsavedChangesSysText: this.unsavedChangesSysText,
              reviewRequestSysText: this.reviewRequestSysText,
              leaveYearOptions: null,
              workday: null
            }
          });

          dialogRef.componentInstance.isEditing.subscribe((res) => {
            this.isEditing.emit(res);
          });

          // after a review opened from this dialog closes, focus would be lost (see the dialog), so it
          // goes to the day's cell, as after a save
          dialogRef.componentInstance.reviewClosed.subscribe(() => {
            this.focusDayCell(event);
          });

          dialogRef.afterClosed().subscribe(() => {
            this.isEditing.emit(false);
          });
        });
    }

    // show a dialog of the leave hours summary only if working with 
    // a case and extended hours are available

    else if (this.caseDetails && eventProps.employeeLeaveHoursExtended) {
      this.dialog.open<ViewCalendarItemComponent, ViewCalendarItemComponentData>(ViewCalendarItemComponent, {
        id: ViewCalendarItemComponent.componentName,
        width: "1000px",
        panelClass: "mat-dialog-container-mobileWidth",
        disableClose: false,
        data: { 
          event: event,
          sysText: this.viewCalendarItemSysText,
          employeeRecord: null,
          caseDetails: this.caseDetails,
          unsavedChangesSysText: null,
          reviewRequestSysText: null,
          leaveYearOptions: this.caseDetails.leaveCalendar?.leaveYearOptions,
          workday: eventProps.workCalendarDay
        }
      });
    }
  }

  private flagCellEvents(
    calendarPageData: LeaveCalendarPageData,
    events: EventInput[],
    cdli: LeaveInfo,
    calendarStartDateFormed: string,
    calendarEndDateFormed: string,
    caseStartDateFormed:string,
    caseEndDateFormed: string): void {
    // update '* cell background' events to be flagged if necessary for all workdays
    // within the case timeframe, or on any calendar date with leaves for this case

    const filteredHoursExtended = calendarPageData?.employeeLeaveHoursExtended?.filter(xhours => {
      const dateFormed = this.datePipe.transform(new Date(xhours.calendarDate), 'yyyy-MM-dd');

      return calendarStartDateFormed <= dateFormed
        && dateFormed <= calendarEndDateFormed;
    });
    
    filteredHoursExtended?.forEach(xhours => {
      const calendarDateFormed = this.datePipe.transform(new Date(xhours.calendarDate), 'yyyy-MM-dd');

      const event = events.find(e => {
        const customProps = e.customProps as CustomCalendarEventProps;
        return e.start == calendarDateFormed 
        && (customProps?.eventType == CalendarEventTypes.BgWorkdayCell 
        || customProps?.eventType == CalendarEventTypes.BgNonWorkdayCell
        || customProps?.eventType == CalendarEventTypes.BgBlankCell
        || customProps?.eventType == CalendarEventTypes.BgBlankCellNoTitle);
      });

      const workday = calendarPageData?.workCalendarDays.find(wcd => 
        this.datePipe.transform(new Date(wcd.date), 'yyyy-MM-dd', 'UTC') == calendarDateFormed);

      // ...show 'leave date warning' if a leave is outside the timeframe of the case,
      // not on a workday and workday restriction is enabled, exceeds work hours and
      // workday hours restriction is enabled, or if full day restrictions apply and
      // hours are not a full workday

      const bank1FullDayWarning = cdli?.bank1RestrictToFullDayIncrements
        && xhours.leaveCaseBank1EffectiveHours != null 
        && (!workday || xhours.leaveCaseBank1EffectiveHours != workday?.fullDayIncrement);

      const bank2FullDayWarning = cdli?.bank2RestrictToFullDayIncrements
        && xhours.leaveCaseBank2EffectiveHours != null 
        && (!workday || xhours.leaveCaseBank2EffectiveHours != workday?.fullDayIncrement);

      const bank3FullDayWarning = cdli?.bank3RestrictToFullDayIncrements
        && xhours.leaveCaseBank3EffectiveHours != null 
        && (!workday || xhours.leaveCaseBank3EffectiveHours != workday?.fullDayIncrement);

      const bank4FullDayWarning = cdli?.bank4RestrictToFullDayIncrements
        && xhours.leaveCaseBank4EffectiveHours != null 
        && (!workday || xhours.leaveCaseBank4EffectiveHours != workday?.fullDayIncrement);

      const bank5FullDayWarning = cdli?.bank5RestrictToFullDayIncrements
        && xhours.leaveCaseBank5EffectiveHours != null 
        && (!workday || xhours.leaveCaseBank5EffectiveHours != workday?.fullDayIncrement);

      const fullDayWarning = bank1FullDayWarning 
        || bank2FullDayWarning
        || bank3FullDayWarning
        || bank4FullDayWarning
        || bank5FullDayWarning;

      if (event
        && this.caseDetails != null
        && xhours.leaveCaseHoursID != null
        && (
          caseStartDateFormed == null
          || calendarDateFormed < caseStartDateFormed
          || caseEndDateFormed == null
          || calendarDateFormed > caseEndDateFormed
          || (calendarPageData.restrictHoursToWorkdays && (workday == null || workday.includeActualSourceHours != true))
          || (calendarPageData.restrictHoursToWorkdayHours && (workday == null || xhours.leaveCaseLeaveHours > workday.actualSourceHours))
          || this.checkForOverdraw(cdli, xhours)
          || fullDayWarning)) {
        event.classNames = ['calendar-warning'];
        event.customProps.eventFlag = CalendarEventFlags.Warning;
        event.customProps.flagText = this.sysText.warning;

        if (this.caseDetails && xhours) {
          event.classNames = [ ...event.classNames, 'calendar-pointer' ];
        }
      }

      // ...show 'exhausted' if workday and there are no draws and available hours is zero

      else if (event
        && xhours.leaveCaseHoursID == null
        && this.checkForExhausted(cdli, xhours)) {
        event.classNames = ['calendar-exhasuted'];
        event.customProps.eventFlag = CalendarEventFlags.Exhausted;
        event.customProps.flagText = this.sysText.exhausted;

        if (this.caseDetails && xhours) {
          event.classNames = [ ...event.classNames, 'calendar-pointer' ];
        }
      }
    });
  }

  // #endregion event functions

  // #region create event functions

  private createWorkCalendarCellEvents(
    calendarPageData: LeaveCalendarPageData,
    nonBlankCells: string[], 
    events: EventInput[]): void {
    if (calendarPageData?.workCalendarDays) {
      calendarPageData?.workCalendarDays.forEach(wcd => {
        const dateFormed = this.datePipe.transform(wcd.date, 'yyyy-MM-dd', 'UTC');
        const currentView = this.currentView;

        const xhours = calendarPageData?.employeeLeaveHoursExtended
          .find(xhours => this.datePipe.transform(xhours.calendarDate, 'yyyy-MM-dd') == dateFormed);

        const leaveHoursRequests = calendarPageData?.employeeLeaveHoursRequests
          ?.filter(lhr => this.datePipe.transform(lhr.calendarDate, 'yyyy-MM-dd') == dateFormed);

        // workdays

        if (wcd.effectiveWorkday) {
          nonBlankCells.push(dateFormed);

          const customProps: CustomCalendarEventProps = {
            eventType: CalendarEventTypes.BgWorkdayCell,
            workCalendarDay: wcd,
            employeeLeaveHoursExtended: xhours,
            employeeLeaveHoursRequests: leaveHoursRequests,
            restrictHoursToWorkdays: calendarPageData.restrictHoursToWorkdays,
            restrictHoursToWorkdayHours: calendarPageData.restrictHoursToWorkdayHours
          };

          const event: EventInput = {
            title: this.sysText.workday + " " + wcd.effectiveHours + "h",
            start: dateFormed,
            classNames: ['calendar-workday'],
            opacity: '1', // override --fc-bg-event-opacity: 0.3
            display: currentView.startsWith('calendar') ? 'background' : 'block',
            customProps: customProps
          };

          // the background of a calendar day is only clickable on a case
          // where extended hours are available (to show the leave hours
          // summary dialog)

          if (this.caseDetails && xhours) {
            event.classNames = [ ...event.classNames, 'calendar-pointer'];
          }

          events.push(event);
        }

        // non-workdays

        else if (wcd.effectiveWorkday == false) {
          nonBlankCells.push(dateFormed);

          let eventTitle: string = '';

          if (!wcd.isOverride) {
            wcd.scheduledNonWorkdays?.forEach((nwd, i)=> {
              eventTitle += ' ' + nwd.name;
              if (i != (wcd.scheduledNonWorkdays.length - 1)) {
                eventTitle += ', ';
              }
            });
          }
          else {
            eventTitle =  this.sysText.non_workday;
          }
          
          const customProps: CustomCalendarEventProps = {
            eventType: CalendarEventTypes.BgNonWorkdayCell,
            workCalendarDay: wcd,
            employeeLeaveHoursExtended: xhours,
            restrictHoursToWorkdays: calendarPageData.restrictHoursToWorkdays,
            restrictHoursToWorkdayHours: calendarPageData.restrictHoursToWorkdayHours
          };

          const event: EventInput = {
            title: eventTitle,
            start: dateFormed,
            classNames: ['calendar-non-workday'],
            opacity: '1', // override --fc-bg-event-opacity: 0.3
            display: currentView.startsWith('calendar') ? 'background' : 'block',
            customProps: customProps
          };

          // the background of a calendar day is only clickable on a case
          // where extended hours are available (to show the leave hours
          // summary dialog)

          if (this.caseDetails && xhours) {
            event.classNames = [ ...event.classNames, 'calendar-pointer' ];
          }

          events.push(event);
        }
      });
    }
  }

  private createBlankCellEvents(
    calendarPageData: LeaveCalendarPageData,
    cd: CaseDetails,
    nonBlankCells: string[],
    events: EventInput[],
    calendarStartDate: Date,
    calendarEndDate: Date): void {
    // create 'blank cell background' events for any day that's not a workday/non-worday

    const currentView = this.currentView;

    for (let calendarDate = new Date(calendarStartDate); calendarDate <= new Date(calendarEndDate); calendarDate.setDate(calendarDate.getDate() + 1)) {
      const dateFormed = this.datePipe.transform(calendarDate, 'yyyy-MM-dd', 'UTC');

      if (!nonBlankCells.includes(dateFormed)) {
        const xhours = calendarPageData?.employeeLeaveHoursExtended
          ?.find(xhours => this.datePipe.transform(xhours.calendarDate, 'yyyy-MM-dd') == dateFormed);

        const leaveHoursRequests = calendarPageData?.employeeLeaveHoursRequests
          ?.filter(lhr => this.datePipe.transform(lhr.calendarDate, 'yyyy-MM-dd') == dateFormed);

        const wcd = calendarPageData?.workCalendarDays
          ?.find(wcd => this.datePipe.transform(new Date(wcd.date), 'yyyy-MM-dd', 'UTC') == dateFormed);

        let event: EventInput;

        if (this.employeeRecord 
          || (cd?.leaveCalendar?.canChangeWorkdays && cd?.employeeInformation.isAnonymous == false)) {
          const customProps: CustomCalendarEventProps = {
            eventType: CalendarEventTypes.BgBlankCell,
            employeeLeaveHoursExtended: xhours,
            employeeLeaveHoursRequests: leaveHoursRequests,
            restrictHoursToWorkdays: calendarPageData.restrictHoursToWorkdays,
            restrictHoursToWorkdayHours: calendarPageData.restrictHoursToWorkdayHours
          };

          // if representative workday, add wcd info
          if (wcd?.isRepresentativeWorkday) {
            customProps.workCalendarDay = wcd;
          }

          event = {
            title: '...',
            start: dateFormed,
            classNames: ['calendar-blank-cell'],
            textColor: 'var(--color-fill1-dk)',
            opacity: '1', // override --fc-bg-event-opacity: 0.3
            display: currentView.startsWith('calendar') ? 'background' : 'block',
            customProps: customProps
          };
        }
        else {
          const customProps: CustomCalendarEventProps = {
            eventType: CalendarEventTypes.BgBlankCellNoTitle,
            employeeLeaveHoursExtended: xhours,
            restrictHoursToWorkdays: calendarPageData.restrictHoursToWorkdays,
            restrictHoursToWorkdayHours: calendarPageData.restrictHoursToWorkdayHours
          };

          event = {
            start: dateFormed,
            classNames: ['calendar-blank-cell'],
            opacity: '1', // override --fc-bg-event-opacity: 0.3
            display: currentView.startsWith('calendar') ? 'background' : 'block',
            customProps: customProps
          };
        }

        // the background of a calendar day is only clickable on a case
        // where extended hours are available (to show the leave hours
        // summary dialog)

        if (this.caseDetails && xhours) {
          event.classNames = [ ...event.classNames, 'calendar-pointer' ];
        }

        events.push(event);
      }
    }
  }

  private createHoursWorkedEvents(
    calendarPageData: LeaveCalendarPageData,
    events: EventInput[],
    calendarStartDateFormed: string,
    calendarEndDateFormed: string): void {
    const filteredCalEvents = calendarPageData?.calendarEvents?.filter(ev => {
      const dateFormed = this.datePipe.transform(new Date(ev.eventDate), 'yyyy-MM-dd');
      return calendarStartDateFormed <= dateFormed
        && dateFormed <= calendarEndDateFormed;
    });
    
    filteredCalEvents?.forEach(ev => {
      const eventDateFormed = this.datePipe.transform(new Date(ev.eventDate), 'yyyy-MM-dd');

      if (ev.eventType == CalendarEventTypes.HoursWorked) {
        const customProps: CustomCalendarEventProps = {
          eventType: CalendarEventTypes.HoursWorked
        };

        const event: EventInput = {
          title: ev.message,
          start: eventDateFormed,
          classNames: ['calendar-hours-worked', 'calendar-pointer'],
          customProps: customProps
        };

        events.push(event);
      }
    });
  }

  private createHoursEvents(
    calendarPageData: LeaveCalendarPageData,
    cd: CaseDetails,
    events: EventInput[],
    calendarStartDateFormed: string,
    calendarEndDateFormed: string): void {
    const filteredHoursExtendedNoHours = calendarPageData?.employeeLeaveHoursExtended?.filter(xhours => {
      const dateFormed = this.datePipe.transform(new Date(xhours.calendarDate), 'yyyy-MM-dd');
      return xhours.leaveCaseHoursID != null 
        && calendarStartDateFormed <= dateFormed
        && dateFormed <= calendarEndDateFormed;
    });

    filteredHoursExtendedNoHours?.forEach(xhours => {
      const calendarDateFormed = this.datePipe.transform(new Date(xhours.calendarDate), 'yyyy-MM-dd');

      const workday = calendarPageData?.workCalendarDays.find(wcd => 
        this.datePipe.transform(new Date(wcd.date), 'yyyy-MM-dd', 'UTC') == calendarDateFormed);
      
      const customProps: CustomCalendarEventProps = {
        eventType: CalendarEventTypes.Hours,
        employeeLeaveHoursExtended: xhours,
        workCalendarDay: workday
      };

      const event: EventInput = {
        title: xhours.leaveCaseLeaveHours + 'h ' + this.sysText.leave,
        start: calendarDateFormed,
        classNames: ['calendar-hours'],
        customProps: customProps
      };

      // make this non-background event clickable if the
      // user is allowed to change hours

      if (cd?.leaveCalendar?.canChangeHours) {
        event.classNames = [ ...event.classNames, 'calendar-pointer' ];
      }

      events.push(event);
    });
  }

  private createAddHoursEvents(
    calendarPageData: LeaveCalendarPageData,
    cdli: LeaveInfo,
    events: EventInput[],
    calendarStartDate: Date,
    calendarEndDate: Date,
    caseStartDateFormed:string,
    caseEndDateFormed: string): void {
    // 'add hours' events show add hours button on dates within
    // the case timeframe and if there are no leave hours taken

    for (let calendarDate = new Date(calendarStartDate); calendarDate <= new Date(calendarEndDate); calendarDate.setDate(calendarDate.getDate() + 1)) {
      const calendarDateFormed = this.datePipe.transform(new Date(calendarDate), 'yyyy-MM-dd');

      const workday = calendarPageData?.workCalendarDays.find(wcd => 
        this.datePipe.transform(new Date(wcd.date), 'yyyy-MM-dd', 'UTC') == calendarDateFormed);

      // ...add hours can only occur within the timeframe of the case,
      // and only on workdays if restrict hours is enabled

      if (caseStartDateFormed <= calendarDateFormed && calendarDateFormed <= caseEndDateFormed
        && (!calendarPageData.restrictHoursToWorkdays || workday?.effectiveWorkday == true)) {
        // ...include the add hours button on workdays that don't
        // already have hours, and where hours aren't exhausted

        const xhours = calendarPageData?.employeeLeaveHoursExtended
          ?.find(xhours => this.datePipe.transform(new Date(xhours.calendarDate), 'yyyy-MM-dd') == calendarDateFormed);

        const leaveHoursRequests = calendarPageData?.employeeLeaveHoursRequests
          ?.filter(lhr => this.datePipe.transform(lhr.calendarDate, 'yyyy-MM-dd') == calendarDateFormed);

        if (xhours != null
          && xhours.leaveCaseHoursID == null
          && ((!cdli.isBank1HoursApplicable 
              && !cdli.isBank2HoursApplicable 
              && !cdli.isBank3HoursApplicable 
              && !cdli.isBank4HoursApplicable 
              && !cdli.isBank5HoursApplicable)
            || (cdli.isBank1HoursApplicable && xhours.employeeBank1AvailableHours > 0) 
            || (cdli.isBank2HoursApplicable && xhours.employeeBank2AvailableHours > 0) 
            || (cdli.isBank3HoursApplicable && xhours.employeeBank3AvailableHours > 0) 
            || (cdli.isBank4HoursApplicable && xhours.employeeBank4AvailableHours > 0) 
            || (cdli.isBank5HoursApplicable && xhours.employeeBank5AvailableHours > 0))) {
          const customProps: CustomCalendarEventProps = {
            eventType: CalendarEventTypes.AddHours,
            employeeLeaveHoursExtended: xhours,
            employeeLeaveHoursRequests: leaveHoursRequests,
            workCalendarDay: workday
          };

          const event: EventInput = {
            title: this.sysText.addHours,
            start: calendarDateFormed,
            classNames: ['calendar-pointer', 'calendar-add-hours'],
            customProps: customProps
          };

          events.push(event);
        }
      }
    }
  }

  private createActionItemEvents(
    calendarPageData: LeaveCalendarPageData,
    events: EventInput[],
    calendarStartDateFormed: string,
    calendarEndDateFormed: string): void {
    const filteredCalEvents = calendarPageData?.calendarEvents?.filter(ev => {
      const dateFormed = this.datePipe.transform(new Date(ev.eventDate), 'yyyy-MM-dd');
      return calendarStartDateFormed <= dateFormed
        && dateFormed <= calendarEndDateFormed;
    });
    
    filteredCalEvents?.forEach(ev => {
      const eventDateFormed = this.datePipe.transform(new Date(ev.eventDate), 'yyyy-MM-dd', 'UTC');

      if (ev.eventType == CalendarEventTypes.ActionItem) {
        const customProps: CustomCalendarEventProps = {
          eventType: CalendarEventTypes.ActionItem
        };

        const event: EventInput = {
          title: ev.message,
          start: eventDateFormed,
          classNames: ['calendar-pointer', 'calendar-action-item'],
          customProps: customProps
        };

        events.push(event);
      }
    });
  }

  private createLeaveHoursRequestEvents(
    calendarPageData: LeaveCalendarPageData,
    events: EventInput[],
    calendarStartDateFormed: string,
    calendarEndDateFormed: string): void {
    const filteredCalEvents = calendarPageData?.calendarEvents?.filter(ev => {
      const dateFormed = this.datePipe.transform(new Date(ev.eventDate), 'yyyy-MM-dd');
      return calendarStartDateFormed <= dateFormed
        && dateFormed <= calendarEndDateFormed;
    });
    
    filteredCalEvents?.forEach(ev => {
      const eventDateFormed = this.datePipe.transform(new Date(ev.eventDate), 'yyyy-MM-dd');

      if (ev.eventType == CalendarEventTypes.RequestForLeaveHours) {
        const leaveHoursRequests = calendarPageData?.employeeLeaveHoursRequests
          ?.filter(lhr => this.datePipe.transform(lhr.calendarDate, 'yyyy-MM-dd') == eventDateFormed
            && lhr.leaveHoursRequestId == ev.id);

        const customProps: CustomCalendarEventProps = {
          eventType: CalendarEventTypes.RequestForLeaveHours,
          employeeLeaveHoursRequests: leaveHoursRequests
        };

        const event: EventInput = {
          title: ev.message,
          start: eventDateFormed,
          classNames: ['calendar-pointer', 'calendar-request-hours'],
          customProps: customProps
        };

        events.push(event);
      }
    });
  }

  private createSpacerEvents(
    calendarPageData: LeaveCalendarPageData,
    events: EventInput[],
    calendarStartDate: Date,
    calendarEndDate: Date): void {
    // blank (transparent) spacer events allow space to click for 
    // the cal summary as well as event cell flag text (warning etc.)
    // that may show up a the bottom of the cell
    for (let calendarDate = new Date(calendarStartDate); calendarDate <= new Date(calendarEndDate); calendarDate.setDate(calendarDate.getDate() + 1)) {
      const dateFormed = this.datePipe.transform(new Date(calendarDate), 'yyyy-MM-dd');

      // add extended hours, leave hours requests, and workday info 
      // for this date to the event so when it's clicked on to open
      // the calendar day summary it has all the data it needs

      const xhours = calendarPageData?.employeeLeaveHoursExtended
        ?.find(xhours => this.datePipe.transform(xhours.calendarDate, 'yyyy-MM-dd') == dateFormed);

      const leaveHoursRequests = calendarPageData?.employeeLeaveHoursRequests
        ?.filter(lhr => this.datePipe.transform(lhr.calendarDate, 'yyyy-MM-dd') == dateFormed);

      const wcd = calendarPageData?.workCalendarDays
        ?.find(wcd => this.datePipe.transform(new Date(wcd.date), 'yyyy-MM-dd', 'UTC') == dateFormed);

      const customProps: CustomCalendarEventProps = {
        eventType: CalendarEventTypes.BlankSpacer,
        workCalendarDay: wcd,
        employeeLeaveHoursExtended: xhours,
        employeeLeaveHoursRequests: leaveHoursRequests,
        restrictHoursToWorkdays: calendarPageData.restrictHoursToWorkdays,
        restrictHoursToWorkdayHours: calendarPageData.restrictHoursToWorkdayHours
      }

      const event: EventInput = {
        start: dateFormed,
        classNames: ['blank-spacer-event'],
        customProps: customProps
      }

      // the title is what list views show (and name the summary link by)

      if (this.caseDetails && xhours) {
        event.classNames = [ ...event.classNames, 'calendar-pointer' ];
        event.title = this.sysText.calSummary;
      }

      events.push(event);
    }
  }

  // #endregion create event functions

  // #region arrow functions

  private onTitleClick = (event: EventApi, jsEvent: Event) => {
    if (this.isMouseClick(jsEvent)) {
      this.setCalFocus();
    }

    const dialogConfig: MatDialogConfig = {
      id: AddEditCalendarWorkdayComponent.componentName,
      width: "400px",
      panelClass: "mat-dialog-container-mobileWidth",
      disableClose: false,
      data: { 
        event: event,
        employeeRecord: this.employeeRecord,
        caseDetails: this.caseDetails,
        sysText: this.addEditCalWorkdaySysText,
        unsavedChangesSysText: this.unsavedChangesSysText
      }
    };

    this.dialog.open(AddEditCalendarWorkdayComponent, dialogConfig)
      .afterClosed()
      .subscribe((res: boolean | CaseDetails) => {
        // either update redraws every event, so focus goes to the day's cell instead of being lost

        if (res) {
          this.focusDayCell(event);
        }

        if ((res as boolean) === true) {
          // update employee calendar
          this.calendarNeedsUpdate = true;
          this.renderCal();
        } else if ((res as CaseDetails) != null) {
          // updating the store object will trigger the subscription to the
          // store object and cause the calendar to update
          this.caseDetailsStore.caseDetails = res as CaseDetails;
        }
      });
  }

  private onCaseLink = (caseId: number) => {
    this.router.navigate([`${appRoutePaths.LEAVE_ADMIN}/${leaveAdminRoutes.DETAILS}`, caseId]);
  }

  private leaveHourExtentsVisibleRange = (currentDate: Date) => {
    // by default, show the month surrounding the supplied date

    const caseDetails = this.caseDetails;

    let startDate = new Date(currentDate);
    startDate.setDate(1);
    
    let endDate = new Date(startDate);
    endDate.setMonth(endDate.getMonth() + 1);
    endDate.setDate(endDate.getDate() - 1);

    if (caseDetails?.leaveCalendar?.firstLeaveDate) {
      // show the period from the first to the last leave

      startDate = new Date(caseDetails.leaveCalendar.firstLeaveDate);
      endDate = new Date(caseDetails.leaveCalendar.lastLeaveDate);

      // full calendar will stop at the day before a date that has
      // no time (00:00:00), so give the end date some time

      endDate.setSeconds(1);
    }

    if (endDate.getFullYear() - startDate.getFullYear() > 2) {
      // reduce the extents to a period of 2 years

      startDate = new Date(endDate);
      startDate.setFullYear(startDate.getFullYear() - 2);
    }

    return { start: startDate, end: endDate };
  }

  private monthTitleFormat = (arg: VerboseFormattingArg) => {
    let title = '';

    const monthDate = new Date(arg.date.year, arg.date.month, arg.date.day);

    if (this.layoutService.isMobile) {
      title = monthDate.toLocaleString('default', { month: 'short', year: 'numeric'});
    }
    else {
      title = monthDate.toLocaleString('default', { month: 'long', year: 'numeric'});
    }

    return title;
  }

  private leaveHourExtentsTitleFormat = (arg: VerboseFormattingArg) => {
    let title = '';

    const startDate = new Date(arg.date.year, arg.date.month, arg.date.day);
    const endDate = new Date(arg.end.year, arg.end.month, arg.end.day);

    if (this.layoutService.isMobile) {
      if (arg.date.year == arg.end.year)
        title = `${arg.date.month}/${arg.date.day} - ${arg.end.month}/${arg.end.day}/${arg.end.year.toString().substring(2,4)}`;
      else
        title = `${arg.date.month}/${arg.date.day}/${arg.date.year.toString().substring(2,4)} - ${arg.end.month}/${arg.end.day}/${arg.end.year.toString().substring(2,4)}`;
    }
    else {
      if (arg.date.year == arg.end.year)
        title = 
          startDate.toLocaleString('default', { month: 'short', day: 'numeric' }) +
          ' - ' +
          endDate.toLocaleString('default', { month: 'short', day: 'numeric', year: 'numeric' });
      else
        title = 
          startDate.toLocaleString('default', { month: 'short', day: 'numeric', year: 'numeric' }) +
          ' - ' +
          endDate.toLocaleString('default', { month: 'short', day: 'numeric', year: 'numeric' });
    }

    return title;
  }

  private createCaseLink = (title: string, caseId: number, canTakeFocus: boolean) => {
    const eventTitle = document.createElement('span');
    eventTitle.textContent = title;
    const caseLink = document.createElement('a');
    caseLink.className += 'calendar-pointer cell-title-hover'
    caseLink.textContent = ` (${caseId})`;

    // an href makes it a real link that Enter follows (the click handler still navigates); where
    // it can't take focus, the employee record's cases section links to the case too

    if (canTakeFocus) {
      caseLink.href = this.router.serializeUrl(this.router.createUrlTree([`${appRoutePaths.LEAVE_ADMIN}/${leaveAdminRoutes.DETAILS}`, caseId]));
    }

    caseLink.addEventListener('click', (event) => {
      event.stopPropagation();
      event.preventDefault();
      this.onCaseLink(caseId);
    }, false);
    return {domNodes: [eventTitle, caseLink]};
  }

  // #endregion arrow functions
}